import { useEffect, useRef, useState } from 'react';
import { Input } from '../../ui';
import { useAdminApi } from '../../hooks/useAdminApi';

interface DemoRequest {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  job_title: string;
  company_name: string;
  company_size: string;
  industry: string;
  preferred_date: string;
  preferred_time: string;
  demo_type: string;
  specific_needs: string | null;
  current_challenges: string | null;
  status: string;
  priority: string;
  assigned_to: string | null;
  notes: string | null;
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  scheduled: 'bg-indigo-100 text-indigo-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  no_show: 'bg-orange-100 text-orange-800',
  converted: 'bg-emerald-100 text-emerald-800',
  lost: 'bg-gray-100 text-gray-800',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const VALID_STATUSES = ['new', 'contacted', 'scheduled', 'completed', 'cancelled', 'no_show', 'converted', 'lost'];

export default function AdminDemoRequests() {
  const [requests, setRequests] = useState<DemoRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const api = useAdminApi();

  const fetchRequests = () => {
    api.get('/api/admin/demo-requests')
      .then((res) => setRequests(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const initRef = useRef(false);
  useEffect(() => { if (!initRef.current) { initRef.current = true; fetchRequests(); } }, [api]);

  const handleStatusUpdate = async (id: number, status: string) => {
    try {
      await api.put(`/api/admin/demo-requests/${id}/status`, { status });
      fetchRequests();
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  const filtered = requests.filter((r) => {
    if (statusFilter && r.status !== statusFilter) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return `${r.first_name} ${r.last_name}`.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      r.company_name.toLowerCase().includes(q);
  });

  if (loading) return <div className="py-12 text-center text-muted-foreground">Loading demo requests...</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">Demo Requests ({requests.length})</h2>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border rounded-md px-2 py-1.5"
          >
            <option value="">All Statuses</option>
            {VALID_STATUSES.map((s) => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </select>
          <Input
            placeholder="Search..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-xs"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-3 py-2 text-left font-medium">Name</th>
              <th className="px-3 py-2 text-left font-medium">Company</th>
              <th className="px-3 py-2 text-left font-medium">Industry</th>
              <th className="px-3 py-2 text-left font-medium">Demo Date</th>
              <th className="px-3 py-2 text-left font-medium">Priority</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
              <th className="px-3 py-2 text-left font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <>
                <tr key={r.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.first_name} {r.last_name}</div>
                    <div className="text-xs text-muted-foreground">{r.email}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div>{r.company_name}</div>
                    <div className="text-xs text-muted-foreground">{r.company_size} employees</div>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{r.industry}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.preferred_date ? new Date(r.preferred_date).toLocaleDateString() : '—'}
                    <div className="text-xs text-muted-foreground">{r.preferred_time}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[r.priority] || ''}`}>
                      {r.priority}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[r.status] || ''}`}>
                      {r.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={r.status}
                      onChange={(e) => handleStatusUpdate(r.id, e.target.value)}
                      className="text-xs border rounded px-1 py-0.5"
                    >
                      {VALID_STATUSES.map((s) => (
                        <option key={s} value={s}>{s.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </td>
                </tr>
                {expandedId === r.id && (
                  <tr key={`${r.id}-detail`}>
                    <td colSpan={7} className="px-3 py-3 bg-muted/20">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><span className="font-medium">Phone:</span> {r.phone}</div>
                        <div><span className="font-medium">Job Title:</span> {r.job_title}</div>
                        <div><span className="font-medium">Demo Type:</span> {r.demo_type}</div>
                        <div><span className="font-medium">Submitted:</span> {new Date(r.created_at).toLocaleDateString()}</div>
                      </div>
                      {r.specific_needs && (
                        <div className="mt-2"><span className="font-medium">Specific Needs:</span> {r.specific_needs}</div>
                      )}
                      {r.current_challenges && (
                        <div className="mt-1"><span className="font-medium">Challenges:</span> {r.current_challenges}</div>
                      )}
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            {filter || statusFilter ? 'No demo requests match your filters' : 'No demo requests yet'}
          </div>
        )}
      </div>
    </div>
  );
}
